import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../../auth/auth-context';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const maybe = error as { response?: { data?: { message?: string } } };
    if (maybe.response?.data?.message) return maybe.response.data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Registration failed. Check your token and credentials.';
}

type RegisterForm = {
  email: string;
  password: string;
  inviteToken: string;
};

export default function Register() {
  const [form, setForm] = useState<RegisterForm>({
    email: '',
    password: '',
    inviteToken: '',
  });
  const [error, setError] = useState('');
  const { register } = useAuth();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    try {
      await register(form.email, form.password, form.inviteToken);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    }
  };

  const isDisabled = !(form.email && form.password && form.inviteToken);

  return (
    <Box
      component="main"
      sx={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper elevation={4} sx={{ p: 4, maxWidth: 360, width: '100%' }}>
        <Typography variant="h4" align="center" gutterBottom>
          Register
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            {error && (
              <Typography color="error" align="center">
                {error}
              </Typography>
            )}
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Invite Token"
              name="inviteToken"
              type="text"
              value={form.inviteToken}
              onChange={handleChange}
              required
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isDisabled}
            >
              Register
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" align="center" sx={{ mt: 2, fontSize: 15 }}>
          Already have an account?{' '}
          <Link component={RouterLink} to="/login" underline="hover">
            Log In
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
}
